import { Component } from "react";

/**
 * ErrorBoundary — catches React render/lifecycle errors anywhere in its subtree
 * and renders a graceful fallback instead of whiting out the entire page.
 *
 * Usage:
 *   <ErrorBoundary fallback={<p>Something went wrong.</p>}>
 *     <SomeComponent />
 *   </ErrorBoundary>
 *
 * Or with the default fallback (a plain card with an error message):
 *   <ErrorBoundary>
 *     <SomeComponent />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console so it still shows in devtools / Sentry etc.
    console.error("[ErrorBoundary] Caught render error:", error, info);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <p className="font-semibold">Something went wrong rendering this section.</p>
          {this.state.error?.message && (
            <p className="mt-1 text-xs text-red-500 font-mono">{this.state.error.message}</p>
          )}
          <button
            className="mt-2 text-xs underline text-red-600"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
